"use client";

import { useState, useEffect, useCallback } from "react";
import { X, ZoomIn } from "lucide-react";
import { cn } from "@/lib/utils";

interface ClinicalImageProps {
  src?: string;
  alt: string;
  caption?: string;
  type?: string;
  children?: React.ReactNode;
}

const typeLabels: Record<string, string> = {
  fundus: "Fundus Photo",
  "slit-lamp": "Slit Lamp",
  oct: "OCT",
  fag: "FAG",
  "visual-field": "Visual Field",
  external: "External",
  "clinical-photo": "Clinical Photo",
  surgical: "Surgical",
  histology: "Histology",
};

export function ClinicalImage({
  src,
  alt,
  caption,
  type = "clinical-photo",
}: ClinicalImageProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, closeLightbox]);

  if (!src) return null;

  return (
    <>
      <figure className="my-4 overflow-hidden rounded-xl border border-border">
        <div
          className="relative bg-black cursor-zoom-in group"
          onClick={() => setLightboxOpen(true)}
        >
          <img
            src={src}
            alt={alt}
            className="mx-auto max-h-96 object-contain"
            loading="lazy"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
            <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
          </div>
        </div>
        <figcaption className="flex items-center gap-2 border-t bg-muted/30 px-4 py-2.5">
          <span
            className={cn(
              "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
              "bg-primary/10 text-primary"
            )}
          >
            {typeLabels[type] || type}
          </span>
          <span className="text-xs text-muted-foreground">
            {caption || alt}
          </span>
        </figcaption>
      </figure>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            aria-label="닫기"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          <div
            className="max-h-[90vh] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={src}
              alt={alt}
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-lg"
            />
            <p className="mt-3 text-center text-sm text-white/80">
              {caption || alt}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
